import React, { useState } from 'react';
import {
  AutocompleteCloseReason,
  createFilterOptions,
  FilterOptionsState,
} from '@material-ui/lab';

import { DictionaryEntry } from 'app/services/dict.service';
import { UtilService } from 'app/services/util.service';
import { isAutofillChange } from 'app/shared/helpers/isAutofillChange';
import { AutoSuggestControlProps } from 'app/components/Controls/AutoSuggestControl/AutoSuggestControl.interfaces';

export function useAutoSuggestControl({
  dictionary,
  handleChange,
  handleFocus,
  handleBlur,
  formatValue,
  input,
  meta: { touched, valid },
  onControlBlur,
  openOnFocus,
  noLimit,
}: AutoSuggestControlProps) {
  const [focused, setFocused] = useState(false);
  const error = touched && !valid;
  const isValueCheckVisible =
    !focused && UtilService.hasValue(input.value) && !error;
  const [open, setOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [initialInputChange, setInitialInputChange] = useState(true);
  const disableClearable = !input.value.length;
  const hasSuggestions = Boolean(dictionary && dictionary.length > 0);

  const onClose = (
    event: React.ChangeEvent<{}>,
    reason: AutocompleteCloseReason
  ) => {
    if (openOnFocus && reason === 'toggleInput') {
      return;
    }

    setOpen(false);
    setIsTyping(false);
  };

  const onOpen = () => {
    if (!openOnFocus || !hasSuggestions) {
      return;
    }

    setOpen(true);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (handleFocus) {
      handleFocus(e.target.value);
    }
    setFocused(true);

    if (openOnFocus && hasSuggestions) {
      setIsTyping(false);
      setOpen(true);
    }
  };

  const inputHandleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: string,
    reason: string
  ) => {
    const isTypedChange = reason === 'input';

    if (initialInputChange && reason !== 'input') {
      setInitialInputChange(false);
      return;
    }

    const isValueAccepted = handleChange(value);

    if (isValueAccepted && isTypedChange && isAutofillChange(e.target)) {
      const formattedValue = formatValue ? formatValue(value) : value;

      if (handleBlur) {
        handleBlur(value);
      }

      input.onBlur(formattedValue);

      if (onControlBlur) {
        onControlBlur(undefined, true, false, true, formattedValue);
      }

      setOpen(false);

      return;
    }

    if (openOnFocus && !UtilService.hasValue(value)) {
      setIsTyping(false);
      setOpen(focused && hasSuggestions);
      return;
    }

    setIsTyping(isTypedChange);

    const shouldOpen = openOnFocus
      ? isTypedChange && focused
      : value.length >= 2 && reason !== 'reset' && focused;

    setTimeout(() => {
      setOpen(shouldOpen);
    }, 0);
  };

  const inputHandleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    setIsTyping(false);

    input.onBlur(e);
    if (onControlBlur) {
      setTimeout(() => {
        onControlBlur();
      }, 0);
    }
    if (handleBlur) {
      handleBlur(input.value);
    }
  };

  const suggestions = dictionary
    ? dictionary.map((item: DictionaryEntry) => item.value)
    : [];

  const matchingFilterOptions = createFilterOptions<string>({
    limit: noLimit ? undefined : 10,
    matchFrom: 'start',
  });

  const showsWholeDictionary = Boolean(openOnFocus) && !isTyping;

  const filterOptions = showsWholeDictionary
    ? (options: Array<string>, state: FilterOptionsState<string>) =>
        matchingFilterOptions(options, { ...state, inputValue: '' })
    : matchingFilterOptions;

  return {
    autoHighlight: !showsWholeDictionary,
    disableClearable,
    error,
    filterOptions,
    focused,
    handleBlur: inputHandleBlur,
    inputHandleChange,
    isValueCheckVisible,
    onClose,
    onFocus,
    onOpen,
    open: open && focused,
    suggestions,
  };
}
